import type { FC, ReactNode } from 'react'
import type { CategoryRef } from '@/types'

import Link from 'next/link'
import { Logo } from '@/components/generals/logo'
import { WhatsAppLink } from '@/components/shop/whatsapp-link'
import { buildCustomRequestMessage, buildQuestionMessage } from '@/lib/whatsapp'
import { siteConfig } from '@/constants/site'

interface SiteFooterProps {
    categories: Array<CategoryRef>
}

export const SiteFooter: FC<SiteFooterProps> = ({ categories }): ReactNode => {
    return (
        <footer className='relative overflow-hidden bg-cocoa-900 text-cream-100'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-px bg-[repeating-linear-gradient(90deg,#e8a5b0_0_10px,transparent_10px_18px)] opacity-60' />
            <div className='mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:py-20'>
                <div className='max-w-sm'>
                    <Logo tone='light' />
                    <p className='mt-5 text-sm leading-relaxed text-cream-200/80'>
                        Handmade crochet dolls, stitched one round at a time with soft cotton yarn and a lot of love. Every piece is made by hand, so no two are ever exactly alike.
                    </p>
                    <WhatsAppLink message={buildQuestionMessage} className='mt-6' size='sm'>
                        Chat on WhatsApp
                    </WhatsAppLink>
                </div>

                <nav aria-labelledby='footer-shop'>
                    <p id='footer-shop' className='text-xs font-bold uppercase tracking-[0.22em] text-blush-200'>
                        Shop
                    </p>
                    <ul className='mt-4 space-y-2.5 text-sm'>
                        <li>
                            <Link href='/shop' className='text-cream-200/85 transition-colors hover:text-white'>
                                All dolls
                            </Link>
                        </li>
                        {categories.map(category => (
                            <li key={category.id}>
                                <Link href={`/shop?category=${category.slug}`} className='text-cream-200/85 transition-colors hover:text-white'>
                                    <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                        {category.name}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <nav aria-labelledby='footer-explore'>
                    <p id='footer-explore' className='text-xs font-bold uppercase tracking-[0.22em] text-blush-200'>
                        Explore
                    </p>
                    <ul className='mt-4 space-y-2.5 text-sm'>
                        <li>
                            <Link href='/' className='text-cream-200/85 transition-colors hover:text-white'>
                                3D boutique
                            </Link>
                        </li>
                        <li>
                            <Link href='/#story' className='text-cream-200/85 transition-colors hover:text-white'>
                                How each doll is made
                            </Link>
                        </li>
                        <li>
                            <Link href='/#how-to-order' className='text-cream-200/85 transition-colors hover:text-white'>
                                How to order
                            </Link>
                        </li>
                        <li>
                            <Link href='/wishlist' className='text-cream-200/85 transition-colors hover:text-white'>
                                Your wishlist
                            </Link>
                        </li>
                    </ul>
                </nav>

                <div>
                    <p className='text-xs font-bold uppercase tracking-[0.22em] text-blush-200'>
                        Custom orders
                    </p>
                    <p className='mt-4 text-sm leading-relaxed text-cream-200/80'>
                        Dreaming of a doll that looks like someone you love, or your pet? Tell us about it and we will plan every colour together.
                    </p>
                    <WhatsAppLink message={buildCustomRequestMessage} variant='ghost' size='sm' className='mt-4 -ml-4 text-blush-100 hover:bg-white/5'>
                        Start a custom request
                    </WhatsAppLink>
                </div>
            </div>

            <div className='border-t border-white/10'>
                <div className='mx-auto flex max-w-7xl flex-col gap-2 px-5 py-6 text-xs text-cream-200/60 sm:flex-row sm:items-center sm:justify-between sm:px-8'>
                    <p>
                        <span suppressHydrationWarning>
                            {new Date().getFullYear()}
                        </span>
                        {' '}
                        {siteConfig.name}. All dolls are handmade.
                    </p>
                    <p className='font-heading italic text-cream-200/80'>
                        Every stitch tells a story.
                    </p>
                </div>
            </div>
        </footer>
    )
}
